import {
  DataField,
  EnumObjects,
  EventFlags,
  IActivityScope,
  makeStringDataField,
  TelemetryEvent,
  TelemetryLogger,
  TelemetrySink,
} from '@microsoft/oteljs'
import { OneDSEndpoint, OneDSSink } from '@microsoft/oteljs-1ds'

import { debug, event, trace } from './console-log'
import { log } from './global-log'
import {
  CustomLogDataValueType,
  ILogEntry,
  ILogSink,
  IOTelActivityContext,
  ISinkContext,
} from './interfaces'
import {
  enumerateObjectMembers,
  getEventName,
  getFlattenedData,
} from './log-helpers'

const sourceEventFieldName = 'SourceEvent'
const aggregatedEventName = 'Aggregated'

export const defaultOTelSinkName = 'OTelSink'

export type OTelNamespace = {
  eventNamePrefix?: string
  feature: string
  project: string
}

export type OTelTokens = {
  ariaTenantToken: string
  nexusTenantToken: number
}

export type OTelConfig = OTelNamespace & OTelTokens

export const createNamespaceTml = ({
  ariaTenantToken,
  feature,
  nexusTenantToken,
  project,
}: Pick<
  OTelConfig,
  'ariaTenantToken' | 'feature' | 'nexusTenantToken' | 'project'
>) =>
  (project &&
    feature &&
    ariaTenantToken &&
    nexusTenantToken && {
      Office: {
        [project]: {
          [feature]: {},
          ariaTenantToken,
          nexusTenantToken,
        },
      },
    }) ||
  undefined

export const createEventNamePrefix = ({
  eventNamePrefix,
  feature,
  project,
}: OTelNamespace) => `Office.${project}.${feature}.${eventNamePrefix || ''}`

export class OTelSink implements ILogSink {
  private readonly eventNamePrefix: string

  constructor(
    otelConfig: OTelConfig,
    otelSink: TelemetrySink,
    private readonly aggregateStream = false,
    private readonly telemetryLogger = new TelemetryLogger()
  ) {
    debug(
      otelConfig.ariaTenantToken && otelConfig.nexusTenantToken,
      'must supply tokens'
    )

    this.eventNamePrefix = createEventNamePrefix(otelConfig)

    telemetryLogger.addSink(otelSink)
    telemetryLogger.setTenantToken(
      `Office.${otelConfig.project}`,
      otelConfig.ariaTenantToken,
      otelConfig.nexusTenantToken
    )
  }

  // name of the sink. usually class SinkClass.name
  public name = () => defaultOTelSinkName

  // push the logEntry to sink endpoint
  public writeEntry = (logEntry: ILogEntry) => {
    // send trace/message data
    const messageData = logEntry ? logEntry.messageData : null
    if (messageData) {
      this.sendTraceMessage(logEntry)
    }

    // send event data
    const eventActionData = logEntry ? logEntry.eventActionData : null
    if (eventActionData) {
      this.sendEventEntry(logEntry)
    }
  }

  // nothing to flush
  public flush = () => trace('OTelSink flush')

  public beginEvent = (eventActionType: string, eventFlags: EventFlags) => {
    const eventName = this.getOTelActivityName(eventActionType || 'EventError')

    const activityContext: IOTelActivityContext = {
      activityScope: this.createOTelEvent(eventName, eventFlags),
      aggregatedScope: this.aggregateStream
        ? this.createOTelEvent(aggregatedEventName, eventFlags)
        : undefined,
    }

    debug(
      eventName.indexOf(this.eventNamePrefix) === 0,
      'eventName prefix not expected',
      eventName
    )

    return { context: activityContext }
  }

  // for Gen3 activities
  public endEvent = (logEntry: ILogEntry, sinkContext: ISinkContext) => {
    const activityContext: IOTelActivityContext =
      sinkContext && sinkContext.context
    if (!sinkContext) {
      debug(activityContext, 'activityInfo not defined', sinkContext)

      return
    }

    this.endEventScope(logEntry, activityContext.activityScope)
    if (activityContext.aggregatedScope) {
      this.endEventScope(logEntry, activityContext.aggregatedScope)
    }

    // local trace
    const eventActionData = logEntry && logEntry.eventActionData
    if (eventActionData) {
      event(
        `OTelSink: 0x${eventActionData.eventTagId.toString(16)} ${
          eventActionData.eventActionType
        }`
      )
    }
  }

  // send event data with OTel
  public sendOTelEvent = (telemetryEvent: TelemetryEvent) => {
    debug(telemetryEvent, 'telemetryEvent not defined', telemetryEvent)
    if (!telemetryEvent) {
      return
    }
    this.telemetryLogger.sendTelemetryEvent(telemetryEvent)
  }

  // send event data with OTel
  public sendOTelActivity = (activityScope: IActivityScope) => {
    debug(activityScope, 'activityScope not defined', activityScope)
    if (!activityScope) {
      return
    }

    activityScope.endNow()
  }

  private readonly endEventScope = (
    logEntry: ILogEntry,
    activityScope: IActivityScope
  ) => {
    const eventActionData = logEntry && logEntry.eventActionData
    if (!eventActionData) {
      debug(eventActionData, 'eventActionData not defined', logEntry)

      return
    }

    // add custom data fields
    const { eventFlags, ...filteredLogEntry } = logEntry
    getFlattenedData(
      filteredLogEntry,
      (name: string, value: CustomLogDataValueType) => {
        activityScope.dataFields.push(this.getDataField(name, value))
      }
    )

    const { responseCode, resultType } = eventActionData

    activityScope.success = resultType !== 'Failure' && resultType !== 'Timeout'

    if (responseCode === undefined) {
      activityScope.setResult(0, 'HRESULT')
    } else {
      activityScope.setResult(
        responseCode,
        'ResponseCode',
        eventActionData.eventTagId
      )
    }

    this.sendOTelActivity(activityScope)
  }

  private readonly getDataField = (
    name: string,
    value: CustomLogDataValueType
  ): DataField => {
    switch (typeof value) {
      case 'number': {
        return {
          classification: EnumObjects.DataClassification.SystemMetadata,
          dataType:
            isFinite(value) && Math.floor(value) === value
              ? EnumObjects.DataFieldType.Int64
              : EnumObjects.DataFieldType.Double,
          name,
          value,
        } as DataField
      }
      case 'boolean': {
        return {
          classification: EnumObjects.DataClassification.SystemMetadata,
          dataType: EnumObjects.DataFieldType.Boolean,
          name,
          value,
        } as DataField
      }
      default: {
        return {
          classification: EnumObjects.DataClassification.SystemMetadata,
          dataType: EnumObjects.DataFieldType.String,
          name,
          value: value.toString(),
        } as DataField
      }
    }
  }

  // sends trace/log message event
  private sendTraceMessage(logEntry: ILogEntry): void {
    const messageData = logEntry ? logEntry.messageData : null
    if (!messageData) {
      return
    }

    const dataFieldInformation = {
      correlationId: logEntry.metadata.correlationId,
      eventTagId: messageData.eventTagId,
      logLevel: messageData.logLevel,
      message: messageData.message,
      messageDetails: messageData.messageDetails,
      sessionId: logEntry.metadata.sessionId,
    }

    const messageDataFields: DataField[] = []
    enumerateObjectMembers(
      dataFieldInformation,
      (name: string, value: CustomLogDataValueType) =>
        messageDataFields.push(this.getDataField(name, value))
    )

    this.sendEvent('Message', logEntry.eventFlags, messageDataFields)
    event(
      `OTelSink: 0x${messageData.eventTagId.toString(16)} ${
        messageData.message
      },${dataFieldInformation}`
    )
  }

  // sends event data
  private sendEventEntry(logEntry: ILogEntry): void {
    const eventActionData = logEntry ? logEntry.eventActionData : null
    if (!eventActionData) {
      return
    }

    const eventDataFields: DataField[] = []
    const { eventFlags, ...filteredLogEntry } = logEntry
    getFlattenedData(
      filteredLogEntry,
      (name: string, value: CustomLogDataValueType) => {
        eventDataFields.push(this.getDataField(name, value))
      }
    )

    // send to event stream
    const eventName = getEventName(logEntry) || 'EventError'
    this.sendEvent(eventName, eventFlags, eventDataFields)

    // send to aggregated event stream
    if (eventName && eventName.toLowerCase() !== 'message') {
      const aggregatedEventDataFields = eventDataFields
      aggregatedEventDataFields.push({
        classification: EnumObjects.DataClassification.SystemMetadata,
        dataType: EnumObjects.DataFieldType.String,
        name: sourceEventFieldName,
        value: eventName,
      } as DataField)

      this.sendEvent('Aggregated', eventFlags, eventDataFields)
    }

    event(
      `OTelSink: 0x${eventActionData.eventTagId.toString(16)} ${
        eventActionData.eventActionType
      },${eventDataFields}`
    )
  }

  // send event data with OTel
  private sendEvent(
    appEventName: string,
    eventFlags: EventFlags,
    dataFields?: DataField[]
  ) {
    debug(appEventName, 'eventName not defined', appEventName, dataFields)
    debug(
      dataFields && dataFields.length > 0,
      'dataFieldInfo not valid',
      appEventName,
      dataFields
    )

    const eventName = this.getOTelActivityName(appEventName)
    const telemetryEvent: TelemetryEvent = {
      dataFields,
      eventFlags,
      eventName,
    }

    this.sendOTelEvent(telemetryEvent)
  }

  // send event data with OTel
  private createOTelEvent(
    eventName: string,
    eventFlags: EventFlags
  ): IActivityScope {
    return this.telemetryLogger.createActivity({
      eventFlags,
      eventName,
    })
  }

  private getOTelActivityName(eventName: string): string {
    // eventNamePrefix is already OTelNamespace + event name prefix
    // event names should match [A-Z][A-Za-z0-9]*, so nothing should be between eventNamePrefix and eventName
    return `${this.eventNamePrefix}${eventName}`
  }
}

export function initializeOtelWebLogger(
  otelConfig: OTelConfig,
  telemetrySink: TelemetrySink | undefined,
  sessionId: string,
  enableFullEvents = true
) {
  const persistentDataFields = [
    makeStringDataField('Session.Id', sessionId),
    makeStringDataField('App.Platform', 'Web'),
    makeStringDataField('App.Name', 'OfficeWebSurfaces'),
  ]

  if (telemetrySink) {
    const telemetryLogger = new TelemetryLogger(undefined, persistentDataFields)
    log.addSink(new OTelSink(otelConfig, telemetrySink, false, telemetryLogger))

    return
  }

  const oneDSSink = new OneDSSink(persistentDataFields, {
    endpointUrl: OneDSEndpoint.PUBLIC,
  })

  oneDSSink.setFullEventsEnabled(enableFullEvents)
  log.addSink(new OTelSink(otelConfig, oneDSSink))
}
